import { useEffect, useState } from "react";
import { getJournalToday, getJournalRecent, saveJournal } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Pencil, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "biggest_win", label: "Biggest win", placeholder: "The one thing that mattered today." },
  { key: "biggest_struggle", label: "Biggest struggle", placeholder: "Where you got stuck." },
  { key: "learned", label: "Most valuable thing learned", placeholder: "What clicked." },
  { key: "tomorrow_priority", label: "Tomorrow's priority", placeholder: "One thing. Just one." },
];

const BLANK = {
  completed_today: false, hours: 0, biggest_win: "", biggest_struggle: "", learned: "", tomorrow_priority: "", confidence: 7,
};

export default function Journal() {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("form"); // "form" | "done"
  const [entries, setEntries] = useState([]);
  const [expandedDate, setExpandedDate] = useState(null);

  const loadHistory = () => {
    getJournalRecent().then((d) => setEntries(d.entries || []));
  };

  useEffect(() => {
    getJournalToday().then((d) => {
      if (d && d.date) {
        setForm((f) => ({ ...f, ...d }));
        setView("done");
      }
    });
    loadHistory();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await saveJournal({ ...form, hours: Number(form.hours) || 0 });
      toast.success("Journal saved. Tomorrow starts sharper.");
      setView("done");
      loadHistory();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const startAnother = () => {
    setForm(BLANK);
    setView("form");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="overline">Daily Reflection</p>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-2">
          <span className="text-primary terminal-glow">$</span> End of day, honestly.
        </h1>
        <p className="text-muted-foreground mt-2">Five questions. Compounds into your own learning book.</p>
      </div>

      {view === "done" ? (
        <Card className="bg-card border-primary rounded-md" data-testid="journal-done-screen">
          <CardContent className="p-8 flex flex-col items-center text-center gap-4">
            <CheckCircle2 className="h-10 w-10 text-primary terminal-glow" />
            <div>
              <h2 className="mono text-xl font-bold">Today's entry is done.</h2>
              <p className="text-muted-foreground mt-1">Saved — biggest win: "{form.biggest_win || "—"}"</p>
            </div>
            <div className="flex gap-2 mt-2">
              <Button
                data-testid="journal-edit-today-btn"
                variant="outline"
                className="border-border mono text-xs"
                onClick={() => setView("form")}
              >
                <Pencil className="h-3 w-3 mr-2" /> Edit today's entry
              </Button>
              <Button
                data-testid="journal-another-entry-btn"
                className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono uppercase tracking-widest text-xs"
                onClick={startAnother}
              >
                Do another entry
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border rounded-md">
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-1 flex items-center justify-between border border-border rounded-md p-3">
                <div>
                  <div className="overline">Completed?</div>
                  <div className="mono text-xs text-muted-foreground mt-1">Did you hit today's plan?</div>
                </div>
                <Switch data-testid="journal-completed-switch" checked={form.completed_today} onCheckedChange={(v) => update("completed_today", v)} />
              </div>
              <div className="sm:col-span-1 border border-border rounded-md p-3">
                <div className="overline mb-1">Hours worked</div>
                <Input data-testid="journal-hours-input" type="number" step="0.5" min="0" value={form.hours} onChange={(e) => update("hours", e.target.value)} className="bg-muted border-border mono" />
              </div>
              <div className="sm:col-span-1 border border-border rounded-md p-3">
                <div className="flex justify-between mono text-xs mb-2">
                  <span className="overline">Confidence</span>
                  <span className="text-primary">{form.confidence}/10</span>
                </div>
                <Slider data-testid="journal-confidence-slider" value={[form.confidence]} min={1} max={10} step={1} onValueChange={(v) => update("confidence", v[0])} />
              </div>
            </div>

            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="overline block mb-2">{f.label}</label>
                <Textarea
                  data-testid={`journal-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => update(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="bg-muted border-border resize-none"
                  rows={2}
                />
              </div>
            ))}

            <div className="flex justify-end">
              <Button data-testid="journal-save-btn" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono uppercase tracking-widest text-xs px-6">
                {saving ? "Saving…" : "Save Reflection"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <div>
          <div className="overline mb-3">Past Entries</div>
          <div className="space-y-2" data-testid="journal-history-list">
            {entries.map((e) => {
              const isOpen = expandedDate === e.date;
              return (
                <Card key={e.date} data-testid={`journal-entry-${e.date}`} className="bg-card border-border rounded-md">
                  <button
                    type="button"
                    data-testid={`journal-entry-toggle-${e.date}`}
                    onClick={() => setExpandedDate(isOpen ? null : e.date)}
                    className="w-full text-left"
                    aria-expanded={isOpen}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="mono text-xs text-primary shrink-0">{e.date}</span>
                          {!isOpen && (
                            <span className="text-sm text-muted-foreground truncate">{e.biggest_win || "—"}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="mono text-[10px] border-border">{e.hours}h</Badge>
                          <Badge variant="outline" className="mono text-[10px] border-border hidden sm:inline-flex">Confidence {e.confidence}/10</Badge>
                          {e.completed_today && (
                            <Badge variant="outline" className="mono text-[10px] border-primary text-primary hidden sm:inline-flex">Hit plan</Badge>
                          )}
                          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
                        </div>
                      </div>

                      {isOpen && (
                        <div className="mt-3 pt-3 border-t border-border space-y-2">
                          <p className="text-sm"><span className="text-muted-foreground">Win: </span>{e.biggest_win || "—"}</p>
                          <p className="text-sm"><span className="text-muted-foreground">Struggle: </span>{e.biggest_struggle || "—"}</p>
                          <p className="text-sm"><span className="text-muted-foreground">Learned: </span>{e.learned || "—"}</p>
                          <p className="text-sm"><span className="text-muted-foreground">Tomorrow: </span>{e.tomorrow_priority || "—"}</p>
                          <div className="flex items-center gap-2 sm:hidden pt-1">
                            <Badge variant="outline" className="mono text-[10px] border-border">Confidence {e.confidence}/10</Badge>
                            {e.completed_today && (
                              <Badge variant="outline" className="mono text-[10px] border-primary text-primary">Hit plan</Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </button>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
